import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";

import { fadeOverlay, slideInRight } from "../../lib/animations";
import { Button } from "../ui/Button";
import { ConnectButton } from "../wallet/ConnectButton";

type NavLink = {
	to: string;
	label: string;
};

type MobileDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	links: Array<NavLink>;
};

export function MobileDrawer({ isOpen, onClose, links }: MobileDrawerProps) {
	useEffect(() => {
		if (!isOpen) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				onClose();
			}
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 md:hidden">
					{/* Backdrop */}
					<motion.div
						variants={fadeOverlay}
						initial="hidden"
						animate="visible"
						exit="exit"
						className="glass-overlay fixed inset-0"
						onClick={onClose}
						aria-hidden="true"
					/>

					{/* Drawer panel */}
					<motion.div
						variants={slideInRight}
						initial="hidden"
						animate="visible"
						exit="exit"
						className="fixed inset-y-0 right-0 w-72 bg-white shadow-pastel-lg"
					>
						<div className="flex flex-col h-full">
							{/* Header */}
							<div className="flex items-center justify-between border-b border-pastel-border p-4">
								<span className="text-lg font-bold text-pastel-black">
									<span className="mr-1">&#10024;</span>
									0G Vibe
								</span>
								<button
									type="button"
									onClick={onClose}
									className="inline-flex items-center justify-center rounded-lg p-2 text-pastel-gray hover:bg-pastel-light-gray hover:text-pastel-black"
									style={{ minWidth: 44, minHeight: 44 }}
									aria-label="Close navigation menu"
								>
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<title>Close</title>
										<line x1="18" y1="6" x2="6" y2="18" />
										<line x1="6" y1="6" x2="18" y2="18" />
									</svg>
								</button>
							</div>

							{/* Links */}
							<nav className="flex flex-col gap-1 p-4">
								{links.map((link) => (
									<Link
										key={link.label}
										to={link.to}
										onClick={onClose}
										className="flex items-center rounded-lg px-3 py-3 text-base font-medium text-pastel-gray transition-colors hover:bg-pastel-light-gray hover:text-pastel-black"
										style={{ minHeight: 44 }}
									>
										{link.label}
									</Link>
								))}
							</nav>

							{/* Actions */}
							<div className="mt-auto border-t border-pastel-border p-4 space-y-3">
								<ConnectButton />
								<Link to="/launch" onClick={onClose}>
									<Button variant="gradient" size="lg" className="w-full">
										Launch Token
									</Button>
								</Link>
							</div>
						</div>
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	);
}
